import { Injectable } from "@angular/core";
import { PatientView } from "../../model/patient/patient-view";
import { PatientService } from "../patient.service";
import { ValorizationService } from "../valorization/valorization.service";
import { OnLineValidationService } from "../on-line-validation/on-line-validation.service";
import { MedicalAppointmentValidation } from "../../model/medical-appointment-validation";
import { MedicalOrderService } from "../medical-order.service";
import { BillingOrderControlService } from "./billing-order-control.service";
import { StudyReportRegionView } from "../../model/study-report/study-report-region-view";
import { PdfService } from "../pdf.service";
import { SafeResourceUrl } from "@angular/platform-browser";
import { BillingValorizationListener } from "./billing-valorization-listener";
import { StudyReportService } from "../study-report/study-report.service";
import {
  BillingOrderControl,
  BillingValorization,
  BillingMedicalOrder,
} from "src/app/model/billing/billing-order-control";
import { BillingOrderControlEdit } from "src/app/model/billing/billing-order-control-edit";
import { BillingStateFilter } from "../../model/billing/billing-state-filter";

@Injectable()
export class BillingOrdenControlWizardService {
  private billingOrderControl: BillingOrderControl;
  private patient: PatientView;
  private billingValorization: BillingValorization;
  private billingValorizationListeners: BillingValorizationListener[] = [];

  constructor(
    private patientService: PatientService,
    private billingOrderControlService: BillingOrderControlService,
    private medicalOrderService: MedicalOrderService,
    private onLineValidationService: OnLineValidationService,
    private studyReportService: StudyReportService,
    private pdfService: PdfService,
    private valorizationService: ValorizationService
  ) {}

  registerBillingValorizationListener(
    billingValorizationListener: BillingValorizationListener
  ) {
    if (
      this.billingValorizationListeners.indexOf(billingValorizationListener) ===
      -1
    ) {
      this.billingValorizationListeners.push(billingValorizationListener);
    }
  }

  unRegisterBillingValorizationListener(
    billingValorizationListener: BillingValorizationListener
  ) {
    const index = this.billingValorizationListeners.indexOf(
      billingValorizationListener
    );
    if (index > -1) {
      this.billingValorizationListeners.splice(index, 1);
    }
  }

  setBillingOrderControl(billingOrderControl: BillingOrderControl) {
    this.patient = null;
    this.billingValorization = null;
    this.billingOrderControl = billingOrderControl;
  }

  setBillingMedicalOrder(billingMedicalOrder: BillingMedicalOrder) {
    this.billingOrderControl.BillingMedicalOrder = billingMedicalOrder;
   }

  async getRecalculatedBillingValorization(
    billingValorization: BillingValorization
  ): Promise<BillingValorization> {
    return await this.valorizationService.recalculateBillingValorization(
      billingValorization
    );
  }

  async getBillingMedicalOrder(): Promise<BillingMedicalOrder> {
    if (
      !this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit
        .MedicalOrderEditInfo
    ) {
      this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo =
        await this.medicalOrderService.getMedicalOrderEditInfoForBilling(
          this.billingOrderControl.MedicalAppointment.Id
        );
    } else {
      await this.getBillingValorization();
      this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo =
        await this.medicalOrderService.medicalOrderEditInfoForBilling(
          this.billingValorization.ValorizationEdit,
          this.billingOrderControl.MedicalAppointment,
          this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit
            .MedicalOrder,
          this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit
            .MedicalOrderEditInfo
        );
    }
    return this.billingOrderControl.BillingMedicalOrder;
  }

  getTransactionReferenceMessage(): string {
    if (
      this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit
        .MedicalOrderEditInfo &&   this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit
        .MedicalOrderEditInfo.MedicalAppointmentValidation
    ) {
      return this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit
        .MedicalOrderEditInfo.MedicalAppointmentValidation
        .TransactionReferenceMessage;
    } else {
      return "";
    }
  }

  async getPatient(): Promise<PatientView> {
    if (!this.patient) {
      this.patient = await this.patientService.getPatientView(
        this.billingOrderControl.PatientId
      );
    }
    return this.patient;
  }

  getPatientId(): number {
    return this.billingOrderControl.PatientId;
  }

  getMedicalAppointmentId(): number {
    return this.billingOrderControl.MedicalAppointment.Id;
  }

  async getBillingValorization(): Promise<BillingValorization> {
    if (!this.billingValorization) {
      this.setBillingValorization(
        await this.valorizationService.getBillingValorization(
          this.billingOrderControl.MedicalAppointment.Id
        )
      );
    }
    return this.billingValorization;
  }

  async getStudyReportRegionViews(): Promise<StudyReportRegionView[]> {
    return await this.studyReportService.getReportRegionViews(
      this.billingOrderControl.MedicalAppointment.Id
    );
  }

  async getStudyReport(studyReportId: number): Promise<SafeResourceUrl> {
    const report = await this.studyReportService.getStudyReportPdf(
      studyReportId
    );
    const pdfUrl = this.pdfService.base64ToPdfUrl(report.ReportBase64);
    return this.pdfService.pdfUrlToPdfSafeResourceUrl(pdfUrl);
  }

  async setBillingValorization(billingValorization: BillingValorization) {
    this.billingValorization = billingValorization;

    const billingMedicalOrder = await this.getBillingMedicalOrder();
    billingMedicalOrder.MedicalOrderEdit.MedicalOrder.IncompleteMedicalOrderAmount =
      billingMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo.IncompleteMedicalOrderAmount;
    billingMedicalOrder.MedicalOrderEdit.MedicalOrder.MedicalOrderStateId =
      billingMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo.MedicalOrderState.Id;

    this.billingValorizationListeners.forEach((listener) => {
      listener.valorizationChange();
    });
  }

  setPatient(patient: PatientView) {
    this.patient = patient;
    this.billingOrderControl.PatientId = patient.Id;
  }

  // TODO: unificar esto con lo de admisión [CODIGO REPETIDO]
  async executeOnLineOperation(isCancel: boolean): Promise<MedicalAppointmentValidation> {

    const medicalAppointmentValidation =
      await this.onLineValidationService.executeOnLineOperation(
        this.billingOrderControl.MedicalAppointment,
        this.billingOrderControl.BillingMedicalOrder
          .MedicalOrderEdit.MedicalOrder,
        this.billingValorization.ValorizationEdit
      );

    this.billingOrderControl.BillingMedicalOrder.MedicalOrderEdit
        .MedicalOrderEditInfo.MedicalAppointmentValidation = medicalAppointmentValidation;

    if (
      !medicalAppointmentValidation.NoValidate &&
      medicalAppointmentValidation.AliquotTax &&
      this.billingValorization.ValorizationEdit.ValorizationAmount.Valorization
        .AliquotTax.Id !== medicalAppointmentValidation.AliquotTax.Id
    ) {
      this.billingValorization.ValorizationEdit.ValorizationAmount.Valorization.AliquotTax =
        medicalAppointmentValidation.AliquotTax;
      this.billingValorization.ValorizationEdit.ValorizationAmount =
        this.valorizationService.recalculatePatientTaxAmount(
          this.billingValorization.ValorizationEdit.ValorizationAmount
        );
    }

    return medicalAppointmentValidation;
  }

  async save(
    billingStateId?: number,
    noBillingReasonId?: number,
    observation: string = ""
  ): Promise<boolean> {
    const billingOrderControlEdit = new BillingOrderControlEdit();
    billingOrderControlEdit.MedicalAppointment =
      this.billingOrderControl.MedicalAppointment;
    billingOrderControlEdit.BillingMedicalOrder =
      this.billingOrderControl.BillingMedicalOrder;
    billingOrderControlEdit.PatientId = this.patient.Id;
    billingOrderControlEdit.BillingStateId = billingStateId;
    billingOrderControlEdit.NoBillingReasonId = noBillingReasonId;
    billingOrderControlEdit.Observation = observation;
    billingOrderControlEdit.ValorizationEdit =
      this.billingValorization.ValorizationEdit;
    return await this.billingOrderControlService.billingOrderControlSave(
      billingOrderControlEdit
    );
  }

  async rollBackOrderControlState(): Promise<boolean> {
    const medicalAppointmentProcedureIds =
      this.billingValorization.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.filter(
        (v) =>
          v.ValorizationItem.MedicalAppointmentProcedure.ChangeBillingState ===
            true && v.ValorizationItem.MedicalAppointmentProcedure.Id > 0
      ).map((v) => v.ValorizationItem.MedicalAppointmentProcedure.Id);
    return await this.billingOrderControlService.rollBackOrderControlState(
      medicalAppointmentProcedureIds
    );
  }
}
